import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Film, Upload, Trash2, Play, BookOpen, RefreshCw, Camera } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  const days = Math.floor(d / 86400);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export default function RiderMedia({ name, m, title, sub, isAdmin }) {
  const [bio, setBio] = useState(undefined); // undefined=loading
  const [bioRefreshing, setBioRefreshing] = useState(false);
  const [videos, setVideos] = useState(null);
  const [canUpload, setCanUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vtitle, setVtitle] = useState("");
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const played = useRef(new Set());
  const [progress, setProgress] = useState(0);

  const react = async (vid, emoji) => {
    setVideos((vs) => vs.map((v) => {
      if (v.id !== vid) return v;
      const on = v.my_reactions?.[emoji];
      return { ...v, my_reactions: { ...v.my_reactions, [emoji]: !on }, reactions: { ...v.reactions, [emoji]: Math.max(0, (v.reactions?.[emoji] || 0) + (on ? -1 : 1)) } };
    }));
    api.post(`/rider-media/videos/${vid}/react`, { emoji }).catch(() => loadVideos());
  };

  const loadVideos = () => api.get(`/rider-media/videos?name=${encodeURIComponent(name)}`)
    .then((r) => { setVideos(r.data.videos || []); setCanUpload(!!r.data.can_upload); })
    .catch(() => setVideos([]));

  useEffect(() => {
    api.get(`/rider-media/bio?name=${encodeURIComponent(name)}`).then((r) => setBio(r.data)).catch(() => setBio({ bio: null }));
    loadVideos();
  }, [name]);

  const refreshBio = () => {
    setBioRefreshing(true);
    api.get(`/rider-media/bio?name=${encodeURIComponent(name)}&refresh=true`)
      .then((r) => { setBio(r.data); toast.success("Bio refreshed"); })
      .catch(() => toast.error("Couldn't refresh bio"))
      .finally(() => setBioRefreshing(false));
  };

  // Grab a poster frame from the chosen clip (client-side) so the grid looks polished.
  const captureThumb = (file) => new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata"; v.muted = true; v.playsInline = true;
      v.onloadedmetadata = () => { try { v.currentTime = Math.min(1, (v.duration || 2) / 2); } catch { resolve(null); } };
      v.onseeked = () => {
        try {
          const w = Math.min(640, v.videoWidth || 640);
          const h = Math.round((v.videoHeight || 360) * (w / (v.videoWidth || 640)));
          const c = document.createElement("canvas"); c.width = w; c.height = h;
          c.getContext("2d").drawImage(v, 0, 0, w, h);
          c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b); }, "image/jpeg", 0.72);
        } catch { URL.revokeObjectURL(url); resolve(null); }
      };
      v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      v.src = url;
    } catch { resolve(null); }
  });

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    const url = URL.createObjectURL(f);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = async () => {
      URL.revokeObjectURL(url);
      if (v.duration && v.duration > 61) { toast.error("Clips must be 60 seconds or less."); if (fileRef.current) fileRef.current.value = ""; if (camRef.current) camRef.current.value = ""; return; }
      const thumb = await captureThumb(f);
      doUpload(f, v.duration || 0, thumb);
    };
    v.onerror = () => { URL.revokeObjectURL(url); toast.error("Couldn't read that video."); };
    v.src = url;
  };

  const doUpload = async (file, duration, thumb) => {
    setUploading(true);
    setProgress(0);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("title", vtitle || "Run");
    fd.append("duration", String(Math.round(duration * 10) / 10));
    fd.append("file", file);
    if (thumb) fd.append("thumbnail", thumb, "thumb.jpg");
    try {
      await api.post("/rider-media/videos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      toast.success("Run uploaded!");
      setVtitle("");
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
      loadVideos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — try again.");
    } finally { setUploading(false); setProgress(0); }
  };

  const del = async (id) => {
    setVideos((vs) => vs.filter((v) => v.id !== id));
    api.delete(`/rider-media/videos/${id}`).catch(() => { toast.error("Couldn't delete."); loadVideos(); });
  };

  const onPlay = (id) => {
    if (played.current.has(id)) return;
    played.current.add(id);
    api.post(`/rider-media/videos/${id}/play`).catch(() => {});
  };

  const box = m ? "bg-white/5 border-white/10" : "bg-[#FAFBFC] border-[#EEF0F3]";

  return (
    <div className="space-y-4">
      {/* Bio */}
      <div>
        <div className={`flex items-center justify-between mb-1.5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${sub}`}>
            <BookOpen className="w-3.5 h-3.5" /> Bio
          </div>
          {isAdmin && bio !== undefined && (
            <button onClick={refreshBio} disabled={bioRefreshing} data-testid={`rider-bio-refresh-${name}`}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold ${sub} hover:text-[#C8000F] disabled:opacity-50`}>
              <RefreshCw className={`w-3 h-3 ${bioRefreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          )}
        </div>
        {bio === undefined || bioRefreshing ? (
          <div className={`flex items-center gap-2 text-[13px] ${sub}`}><Loader2 className="w-4 h-4 animate-spin" /> {bioRefreshing ? "Re-pulling bio…" : "Pulling bio…"}</div>
        ) : bio?.bio ? (
          <div className={`rounded-xl border p-3 ${box}`} data-testid={`rider-bio-${name}`}>
            <p className={`text-[13px] leading-relaxed whitespace-pre-line ${title}`}>{bio.bio}</p>
            {bio.sources?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {bio.sources.slice(0, 4).map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[10.5px] px-1.5 py-0.5 rounded-md bg-[#C8000F]/10 text-[#C8000F] truncate max-w-[160px]">{s.title}</a>
                ))}
              </div>
            )}
            {bio.generated_at && <div className={`mt-2 text-[10.5px] ${sub}`} data-testid={`rider-bio-updated-${name}`}>Updated {timeAgo(bio.generated_at)}</div>}
          </div>
        ) : (
          <p className={`text-[13px] ${sub}`} data-testid={`rider-bio-empty-${name}`}>No bio available yet.</p>
        )}
      </div>

      {/* Videos */}
      <div>
        <div className={`flex items-center justify-between mb-1.5`}>
          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${sub}`}>
            <Film className="w-3.5 h-3.5" /> Runs {videos?.length ? `(${videos.length})` : ""}
          </div>
        </div>

        {canUpload && (
          <div className={`rounded-xl border p-3 mb-2 ${box}`} data-testid={`rider-upload-${name}`}>
            <div className="text-[12px] font-semibold mb-2" style={{ color: m ? "#E2E8F0" : "#14181F" }}>Upload a run <span className={sub}>(max 60s)</span></div>
            <input value={vtitle} onChange={(e) => setVtitle(e.target.value)} placeholder="Title (e.g. Ponoka — 88.5)" data-testid={`rider-video-title-${name}`}
              className={`w-full text-[13px] rounded-lg border px-3 py-2 mb-2 focus:outline-none focus:border-[#C8000F] ${m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F]"}`} />
            <input ref={fileRef} type="file" accept="video/*" onChange={onPick} disabled={uploading} className="hidden" id={`rm-file-${name}`} data-testid={`rider-video-input-${name}`} />
            <input ref={camRef} type="file" accept="video/*" capture="environment" onChange={onPick} disabled={uploading} className="hidden" id={`rm-cam-${name}`} data-testid={`rider-video-camera-${name}`} />
            <div className="flex items-center gap-2 flex-wrap">
              <label htmlFor={`rm-file-${name}`}
                className={`inline-flex items-center gap-2 text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""} bg-[#C8000F] text-white`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? "Uploading…" : "Choose video"}
              </label>
              {m && (
                <label htmlFor={`rm-cam-${name}`} data-testid={`rider-record-${name}`}
                  className={`inline-flex items-center gap-2 text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer border ${uploading ? "opacity-60 pointer-events-none" : ""} border-white/15 text-slate-200`}>
                  <Camera className="w-4 h-4" /> Record
                </label>
              )}
            </div>
            {uploading && (
              <div className="mt-3" data-testid={`rider-upload-progress-${name}`}>
                <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: m ? "#94A3B8" : "#64748B" }}>
                  <span>Uploading…</span><span className="font-semibold tabular-nums">{progress}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-[#C8000F]/15">
                  <div className="h-full bg-[#C8000F] transition-all duration-200" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
          </div>
        )}

        {videos === null ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#C8000F]" /></div>
        ) : videos.length === 0 ? (
          <p className={`text-[13px] ${sub}`} data-testid={`rider-no-videos-${name}`}>{canUpload ? "You haven't uploaded any runs yet." : "No runs uploaded yet."}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {videos.map((v) => (
              <div key={v.id} className={`rounded-xl border overflow-hidden ${box}`} data-testid={`rider-video-${v.id}`}>
                <video src={`${BACKEND}${v.url}`} poster={v.thumb ? `${BACKEND}${v.thumb}` : undefined} controls playsInline preload="metadata" onPlay={() => onPlay(v.id)}
                  className="w-full bg-black aspect-video" />
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className={`text-[13px] font-semibold truncate ${title}`}>{v.title}</div>
                    <div className={`text-[11px] ${sub} flex items-center gap-1`}><Play className="w-3 h-3" /> {v.plays} play{v.plays === 1 ? "" : "s"}{v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ""}</div>
                  </div>
                  {v.can_delete && (
                    <button onClick={() => del(v.id)} data-testid={`rider-video-delete-${v.id}`} className="shrink-0 p-1.5 rounded-lg text-[#C8000F] hover:bg-[#C8000F]/10"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="flex items-center gap-2 px-3 pb-2.5">
                  {[["like", "👍"], ["cowboy", "🤠"]].map(([em, icon]) => {
                    const on = v.my_reactions?.[em];
                    return (
                      <button key={em} onClick={() => react(v.id, em)} data-testid={`rider-react-${em}-${v.id}`}
                        className={`inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#C8000F]/15 border-[#C8000F]/40 text-[#C8000F]" : (m ? "border-white/10 text-slate-300 hover:bg-white/5" : "border-[#E4E7EB] text-slate-500 hover:bg-[#FAFBFC]")}`}>
                        <span className="text-[14px] leading-none">{icon}</span> {v.reactions?.[em] || 0}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
